/** Presentation policy from the bound model, never a new pricing calculation. */
import {
  discoveryPricingStatus,
  selectedOfferings,
  type ExecutableDiscoveryModel,
} from "@/domain/discovery-model";
import type { DiscoveryAnswers, DiscoveryEstimate } from "@/domain/types";
import { isNumericPricing } from "@/domain/types";

import { projectPricing } from "./pricing-presentation";

export type EstimatePresentation = {
  estimate: DiscoveryEstimate | null;
  scope: "complete" | "partial" | "unavailable";
  pending?: boolean;
  display?: EstimateDisplay;
  excluded: import("@/domain/types").Localized[];
};

/** Derive from the bound (including historical) model; saved results remain unchanged. */
export function presentEstimate(
  model: ExecutableDiscoveryModel,
  answers: DiscoveryAnswers,
  saved: DiscoveryEstimate | null,
): EstimatePresentation {
  const selected = selectedOfferings(model, answers);
  const display = resolveEstimateDisplay(model, answers, saved);
  if (display === "hidden") return { estimate: null, scope: "unavailable", excluded: [] };
  const excluded = selected.filter((o) => !isNumericPricing(o.pricing)).map((o) => o.name);
  const pricing = projectPricing(model, answers);
  if (pricing.origin === "hidden") return { estimate: null, scope: "unavailable", excluded: [] };
  if (pricing.origin === "pending")
    return { estimate: null, scope: "unavailable", excluded, pending: true };
  if (excluded.length && selected.some((o) => isNumericPricing(o.pricing))) {
    return { estimate: pricing.estimate, scope: "partial", excluded };
  }
  if (pricing.unresolved.length)
    return {
      estimate: pricing.estimate,
      scope: pricing.estimate ? "complete" : "unavailable",
      excluded,
      display: pricing.estimate ? pricing.display : "quote-required",
    };
  return {
    estimate: display === "quote-required" ? null : saved,
    scope: display === "quote-required" ? "unavailable" : "complete",
    excluded: [],
  };
}

export type EstimateDisplay = "range" | "from" | "fixed" | "hidden" | "quote-required" | "pending";

export function resolveEstimateDisplay(
  model: ExecutableDiscoveryModel,
  answers: DiscoveryAnswers,
  estimate: DiscoveryEstimate | null,
): EstimateDisplay {
  const selected = selectedOfferings(model, answers);
  // Visibility takes precedence over availability, including mixed selections.
  if (
    model.scenario.pricingDisplay === "hidden" ||
    selected.some((o) => isNumericPricing(o.pricing) && o.pricing.display === "hidden")
  )
    return "hidden";
  const unavailable = discoveryPricingStatus(model, answers);
  if (
    unavailable &&
    !(
      selected.some((o) => isNumericPricing(o.pricing)) &&
      selected.some((o) => !isNumericPricing(o.pricing)) &&
      estimate
    )
  )
    return unavailable;
  if (!estimate) return "quote-required";
  // A starting component makes the aggregate a minimum, even beside a known
  // bounded/fixed component. Its calculated high is not a supported ceiling.
  if (
    model.scenario.pricingDisplay === "from" ||
    selected.some((o) => isNumericPricing(o.pricing) && o.pricing.display === "from")
  )
    return "from";
  // Fixed bases can acquire ranged adjustments. Do not hide a real spread.
  if (
    estimate.low === estimate.high &&
    (model.scenario.pricingDisplay === "fixed" ||
      selected
        .filter((o) => isNumericPricing(o.pricing))
        .every((o) => isNumericPricing(o.pricing) && o.pricing.display === "fixed"))
  )
    return "fixed";
  return "range";
}

export function estimateLabelKey(
  display: EstimateDisplay,
  surface: "summary" | "result" | "title",
  scope?: EstimatePresentation["scope"],
) {
  if (display === "pending") return "journey.pricing.pendingLabel";
  if (scope === "partial") return "disc.partialEstimate";
  if (display === "hidden" || display === "quote-required")
    return surface === "title" ? "disc.detailsReady" : "disc.quoteRequired";
  if (display === "from")
    return surface === "title" ? "disc.result.startingTitle" : "disc.startingPrice";
  if (display === "fixed")
    return surface === "title" ? "disc.result.fixedTitle" : "disc.fixedPrice";
  return surface === "title"
    ? "disc.result.title"
    : surface === "result"
      ? "disc.result.range"
      : "disc.estimate";
}
